import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';

import { i18nTranslate } from 'common/helper/i18n/translate';
import { AutocompleteResultInterface } from 'common/module/autocomplete/result.interface';
import { AutocompleteSectionInterface } from 'common/module/autocomplete/section.interface';
import { AutocompleteStoreEvent } from 'common/module/autocomplete/store.event';
import { AutocompleteStoreInterface } from 'common/module/autocomplete/store.interface';
import { AutocompleteStoreDecorator } from 'common/module/autocomplete/store/decorator';
import { BrokerAutocompleteDecoratorInterface } from 'common/module/broker/autocomplete/decorator.interface';
import { BrokerAutocompleteSectionIdEnum } from 'common/module/broker/autocomplete/section-id.enum';
import { BrokerAutocompleteStoreEvent } from 'common/module/broker/autocomplete/store.event';

export class BrokerAutocompleteDecorator
  extends AutocompleteStoreDecorator
  implements BrokerAutocompleteDecoratorInterface
{
  /**
   * @inheritDoc
   */
  public search: (searchString: string) => void;

  /**
   * Autocomplete sections labels
   */
  protected sectionLabels: DataKeyValueStringInterface = {
    location: i18nTranslate('Locations'),
    broker: i18nTranslate('Companies'),
  };

  /**
   * @inheritDoc
   */
  constructor(
    autocompleteStore: AutocompleteStoreInterface,
    private asyncDebounceHelper: (func: (searchString: string) => void, delay: number) => (searchString: string) => void
  ) {
    super(autocompleteStore);
    this.search = this.asyncDebounceHelper(this.onSearchAsync, 0);

    this.getEventEmitter().addListener(AutocompleteStoreEvent.searchBrokersSuccess, this.onSearchBrokersSuccess);
    this.getEventEmitter().addListener(AutocompleteStoreEvent.searchLocationsSuccess, this.onSearchLocationsSuccess);
  }

  /**
   * @inheritDoc
   */
  public setResult(result: AutocompleteResultInterface): void {
    if (result.sectionId === BrokerAutocompleteSectionIdEnum.broker) {
      this.setBrokerId(result.id);
    }

    if (result.sectionId === BrokerAutocompleteSectionIdEnum.location) {
      this.setLocationId(result.id);
    }
  }

  /**
   * @inheritDoc
   */
  public getAutocompleteResults(): AutocompleteSectionInterface[] {
    const autocompleteStoreState = this.getState();
    const autocompleteResults: AutocompleteSectionInterface[] = [];

    if (autocompleteStoreState.locations && autocompleteStoreState.locations.length) {
      autocompleteResults.push({
        id: BrokerAutocompleteSectionIdEnum.location,
        name: this.sectionLabels.location,
        suggestions: autocompleteStoreState.locations.map((location) => ({
          id: location.id,
          text: location.name,
          description: location.path_name,
          sectionId: BrokerAutocompleteSectionIdEnum.location,
        })),
      });
    }

    if (autocompleteStoreState.brokers && autocompleteStoreState.brokers.length) {
      autocompleteResults.push({
        id: BrokerAutocompleteSectionIdEnum.broker,
        name: this.sectionLabels.broker,
        suggestions: autocompleteStoreState.brokers.map((broker) => ({
          id: broker.id,
          text: broker.name,
          sectionId: BrokerAutocompleteSectionIdEnum.broker,
          description: '',
        })),
      });
    }

    return autocompleteResults;
  }

  /**
   * On search async
   */
  private onSearchAsync = (searchString: string) => {
    this.searchBrokers(searchString);
    this.searchLocations(searchString, {
      'page[limit]': 8,
    });
    this.getEventEmitter().emit(BrokerAutocompleteStoreEvent.searchStart, searchString);
  };

  /**
   * Triggers when brokers found
   */
  private onSearchBrokersSuccess = () => {
    this.getEventEmitter().emit(BrokerAutocompleteStoreEvent.searchSuccess);
  };

  /**
   * Triggers when locations fount
   */
  private onSearchLocationsSuccess = () => {
    this.getEventEmitter().emit(BrokerAutocompleteStoreEvent.searchSuccess);
  };
}
